import _ from 'lodash'
import moment, { Moment } from 'moment'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { ConnectedProps, connect } from 'react-redux'
import { compose } from 'recompose'

import { DEFAULT_DATE_FORMAT } from 'constants/date'
import { AdministerTypeProps, AdministrationStatusProps } from 'services/administrations/constants'

import { flatten, nest } from 'utils/flatnest'

import { withAdministrationsService, withAdministrationsServiceProps } from 'services/administrations'
import { withAppService, withAppServiceProps } from 'services/app'
import { withChildService, withChildServiceProps } from 'services/legacy/child'
import { getMedicationFormExportCriteria } from 'services/medicationForms/single/selectors'
import { withRouter, withRouterProps } from 'services/router'
import { withModalService, withModalServiceProps } from 'services/utils/modal'
import { withPaginationUtils, withPaginationUtilsProps } from 'services/utils/pagination'

import { ModalType } from 'modals'

import { RootState } from 'core/reducers'
import i18n from 'translations'

import { TABLE_COLUMNS } from './constants'
import { getTableData } from './ListHelpers'
import ListView from './ListView'

export interface FilterProps {
  administerType?: AdministerTypeProps
  administrationDateRange?: Moment[]
  administrationStatus?: AdministrationStatusProps
}

const ADMINISTRATIONS_GROUPS = {
  read: [
    'administration',
    'administration.medicationForm',
    'administration.medicationDetails',
    'administration.contactedParent',
    'administration.child',
    'administration.signatures',
    'administrationSignature',
    'administrationSignature.owner',
    'administrationSignature.signature',
    'medicationForm',
    'medicationForm.medicationDetails',
    'medicationForm:medicationDetails',
    'medicationDetails',
    'medicationForm.child',
    'medium',
    'user',
    'child',
    'carer',
    'membership',
  ],
}

type ListContainerProps = withAppServiceProps
  & withModalServiceProps
  & withChildServiceProps
  & withPaginationUtilsProps
  & withAdministrationsServiceProps
  & withRouterProps

const mapState = (state: RootState, {
  administrationsListState,
  administrationsSelectors,
  appSelectors,
  childSelectors,
}: ListContainerProps) => ({
  administrationsList: administrationsSelectors.getAdministrationsListSelector(state),
  child: childSelectors.getChildSelector(state),
  errorMessages: appSelectors.getErrorMessages(administrationsListState),
  isFetching: appSelectors.getIsFetching(administrationsListState),
  totalResults: appSelectors.getTotalResults(administrationsListState),
})

const connector = connect(mapState)

type PropsFromRedux = ConnectedProps<typeof connector>

const ListContainer: React.FC<ListContainerProps & PropsFromRedux> = ({
  administrationsActions,
  administrationsList,
  administrationsSelectors,
  child,
  errorMessages,
  isFetching,
  location,
  modalActions,
  paginationUtils,
  params,
  setLocationQuery,
  totalResults,
}) => {
  const { childId } = params || {}
  const { query } = location
  const {
    administerType: administerTypeQuery,
    administrationDateRange: administrationDateRangeQuery,
    administrationStatus: administrationStatusQuery,
  } = nest(query) || {}

  const { getPaginationDetails, onPageChange, page } = paginationUtils
  const [search, setSearch] = useState<string>()
  const [filters, setFilters] = useState<FilterProps>({
    administerType: administerTypeQuery || null,
    administrationDateRange: administrationDateRangeQuery || null,
    administrationStatus: administrationStatusQuery || null,
  })

  const [isInitialized, setIsInitialized] = useState<boolean>(false)

  const fetchData = () => {
    const { administerType, administrationDateRange, administrationStatus } = filters

    const locationQuery = {
      administerType,
      administrationDateRange: {
        after: administrationDateRange?.[0] && moment(administrationDateRange?.[0])?.format(DEFAULT_DATE_FORMAT),
        before: administrationDateRange?.[1] && moment(administrationDateRange?.[1])?.format(DEFAULT_DATE_FORMAT),
      },
      administrationStatus,
      search,
    }

    setLocationQuery(flatten(locationQuery))

    const criteria = administrationsSelectors.getCriteria({
      activeOnly: true,
      administerType,
      administrationDateRange,
      administrationStatus,
      childId,
      search,
    })

    administrationsActions.list({
      params: {
        criteria,
        groups: ADMINISTRATIONS_GROUPS,
        page,
      },
    })
  }

  useEffect(() => {
    if (!isInitialized) {
      fetchData()
      setIsInitialized(true)
    }

    return () => {
      administrationsActions.clearList()
    }
  }, [])

  useEffect(() => {
    if (isInitialized) {
      if (1 === page) {
        fetchData()
      } else {
        onPageChange()(1)
      }
    }
  }, [
    search,
    filters.administrationDateRange,
    filters.administerType,
    filters.administrationStatus,
  ])

  useEffect(() => {
    if (isInitialized) {
      fetchData()
    }
  }, [page])

  const tableColumns = useMemo(() => {
    if (childId) {
      return TABLE_COLUMNS.filter(({ field }) => 'child' !== field)
    }

    return TABLE_COLUMNS
  }, [childId])

  const handleParentSignatureClick = useCallback((id) => {
    modalActions.show<ModalType.ADMINISTRATION_PARENT_SIGNATURE>(ModalType.ADMINISTRATION_PARENT_SIGNATURE, {
      id,
    })
  }, [])

  const handleExportModalClick = (id: number) => (body, onSuccess, onFailed) => {
    const userCriteria = _.find(body?.criteria, { field: 'membership' })

    const exportBody = getMedicationFormExportCriteria({
      id,
      membershipId: userCriteria?.value,
    })

    administrationsActions.exportAdministration({
      body: exportBody,
      onFailed,
      onSuccess,
    })
  }

  const handleExportClick = useCallback((id: number) => {
    modalActions.show<ModalType.EXPORT_REPORT>(ModalType.EXPORT_REPORT, {
      onSendReport: handleExportModalClick(id),
      successMessageTransKey: 'module:Safeguarding:Administration:List:exportSuccessMessage',
      title: i18n.t('module:Safeguarding:Administration:List:exportTitle'),
      userLabel: i18n.t('module:Safeguarding:Administration:List:exportUserLabel'),
    })
  }, [])

  const administrationsListTableData = useMemo(() => getTableData(
    administrationsList,
    childId,
    handleParentSignatureClick,
    handleExportClick,
  ), [
    administrationsList,
    childId,
    handleParentSignatureClick,
    handleExportClick,
  ])

  const handlePageChange = (newPage) => {
    onPageChange()(newPage)
  }

  const handleAddClick = () => {
    modalActions.show<ModalType.ADD_ADMINISTRATION>(ModalType.ADD_ADMINISTRATION, {
      onSubmitSuccess: fetchData,
      params: {
        childOption: child ? {
          avatar: child.photo,
          label: child.fullName,
          value: child.id,
        } : undefined,
      },
    }, {
      enableMultipleModal: true,
    })
  }

  const handleEmergencyDoseClick = () => {
    modalActions.show<ModalType.ADD_EMERGENCY_DOSE>(ModalType.ADD_EMERGENCY_DOSE, {
      childOption: child ? {
        avatar: child.photo,
        label: child.fullName,
        value: child.id,
      } : undefined,
      onSubmitSuccess: fetchData,
    }, {
      enableMultipleModal: true,
    })
  }

  const handleSearchChange = (newSearch) => {
    setSearch(newSearch)
  }

  const handleAdministrationDateRangeChange = (selectedAdministrationDateRange) => {
    setFilters((oldFilters) => ({
      ...oldFilters,
      administrationDateRange: selectedAdministrationDateRange,
    }))
  }

  const handleAdministrationStatusChange = (selectedAdministrationStatus) => {
    setFilters((oldFilters) => ({
      ...oldFilters,
      administrationStatus: selectedAdministrationStatus?.value,
    }))
  }

  const handleAdministerTypeChange = (selectedAdministerType) => {
    setFilters((oldFilters) => ({
      ...oldFilters,
      administerType: selectedAdministerType?.value,
    }))
  }

  return (
    <ListView
      administrationsListTableData={administrationsListTableData}
      errorMessages={errorMessages}
      filters={filters}
      isLoading={isFetching}
      pagination={getPaginationDetails(totalResults)}
      search={search}
      tableColumns={tableColumns}
      onAddClick={handleAddClick}
      onAdministerTypeChange={handleAdministerTypeChange}
      onAdministrationDateRangeChange={handleAdministrationDateRangeChange}
      onAdministrationStatusChange={handleAdministrationStatusChange}
      onEmergencyDoseClick={handleEmergencyDoseClick}
      onEnquiryDateChange={handleAddClick}
      onPageChange={handlePageChange}
      onSearchChange={handleSearchChange}
    />
  )
}

const enhance = compose(
  withAppService,
  withModalService,
  withChildService,
  withPaginationUtils,
  withAdministrationsService,
  withRouter,
  connector,
)

export default enhance(ListContainer)
